// Modal dialog over the native HTML <dialog> element. Free focus trap,
// free ESC handling, free click-outside-to-close (we wire the last bit
// explicitly because <dialog>'s backdrop click isn't fired by default).
//
// Usage:
//   <Dialog open={open} onClose={...}>...content...</Dialog>
//
// The content is rendered inside .modal-card so consumers get a consistent
// chrome (max-w, padding, rounded card) without having to repeat it.

import { useEffect, useRef } from 'react';

interface DialogProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Optional max-width override (Tailwind class). Default `max-w-md`. */
  widthClass?: string;
}

export function Dialog({ open, onClose, children, widthClass = 'max-w-md' }: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);

  // Drive the <dialog> imperatively because the open attribute alone
  // doesn't trigger the modal + backdrop behavior — only showModal() does.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  // The native cancel event fires on ESC. Re-route to onClose so parent
  // state stays in sync.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    function handleCancel(e: Event) {
      e.preventDefault();
      onClose();
    }
    el.addEventListener('cancel', handleCancel);
    return () => el.removeEventListener('cancel', handleCancel);
  }, [onClose]);

  // Click on the backdrop (anywhere outside .modal-card) closes.
  function onBackdropClick(e: React.MouseEvent<HTMLDialogElement>) {
    if (e.target === ref.current) onClose();
  }

  return (
    <dialog
      ref={ref}
      onClick={onBackdropClick}
      className="rounded-lg bg-transparent p-0 backdrop:bg-slate-900/40"
    >
      <div
        className={'card p-6 ' + widthClass}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </dialog>
  );
}
